import { IPurchaseRequisition, IItem } from "@/types/purchaseRequisition.type";

type Props = {
  requisition: Partial<IPurchaseRequisition>;
  closeModal?: () => void;
};

const PRIORITY_LABELS: Record<string, string> = {
  HIGH: "Alta",
  MEDIUM: "Média",
  LOW: "Baixa",
};

export default function ModalViewPurchaseRequisition({ requisition, closeModal }: Props) {
  const items: IItem[] = requisition.itens ?? [];
  const licensePlate = requisition.licensePlate ?? "";
  const observation = requisition.observation ?? "";
  const createdAt = requisition.date ? new Date(requisition.date) : null;
  const priority =
    requisition.priority && PRIORITY_LABELS[requisition.priority]
      ? PRIORITY_LABELS[requisition.priority]
      : "Média";
  const code = requisition?.code ?? "";
  const requester = requisition?.requestorName ?? "";

  const formatDate = (d?: Date | null) =>
    d ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "2-digit", day: "2-digit" }) : "-";

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 px-4">
      <div
        role="dialog"
        aria-modal="true"
        className="bg-white z-10 w-full max-w-4xl rounded-2xl shadow-2xl p-8 relative overflow-y-auto max-h-[90vh] border border-gray-200"
      >
        <button
          onClick={closeModal}
          aria-label="Close modal"
          className="absolute top-3 right-4 text-gray-500 hover:text-red-500 text-2xl font-bold transition"
        >
          ×
        </button>

        <h2 className="text-2xl font-bold mb-6 text-gray-800 border-b pb-3">
          Requisição de Compra
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="md:col-span-1">
            <p className="text-xs text-gray-500">Código</p>
            <p className="font-semibold text-gray-800">{String(code || "-")}</p>
          </div>

          <div className="md:col-span-1">
            <p className="text-xs text-gray-500">Prioridade</p>
            <p className="font-semibold text-gray-800">
              {priority}
            </p>
          </div>
          <div className="md:col-span-1">
            <p className="text-xs text-gray-500">Placa</p>
            <p className="font-semibold text-gray-800">{licensePlate || "-"}</p>
          </div>
          <div className="md:col-span-1">
            <p className="text-xs text-gray-500">Data</p>
            <p className="font-semibold text-gray-800">{formatDate(createdAt)}</p>
          </div>
          <div className="md:col-span-2">
            <p className="text-xs text-gray-500">Solicitante</p>
            <p className="font-semibold text-gray-800">{requester || "-"}</p>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Itens</h3>
        {items.length === 0 ? (
          <div className="border border-gray-200 rounded-lg p-4 text-sm text-gray-600 mb-6">
            Nenhum item informado.
          </div>
        ) : (
          <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden">
            <div className="grid grid-cols-12 bg-gray-50 text-xs font-semibold text-gray-600 px-4 py-2">
              <div className="col-span-1">#</div>
              <div className="col-span-8">Item</div>
              <div className="col-span-3 text-right">Quantidade</div>
            </div>
            <ul className="divide-y">
              {items.map((it, idx) => (
                <li key={(it)?.id ?? `${it.productId}-${idx}`} className="grid grid-cols-12 px-4 py-3 text-sm">
                  <div className="col-span-1 text-gray-500">{idx + 1}</div>
                  <div className="col-span-8">
                    <p className="font-medium text-gray-800">{it.productName || "-"}</p>
                    {it.deliveryDate ? (
                      <p className="text-xs text-gray-500">Entrega: {formatDate(it.deliveryDate)}</p>
                    ) : null}
                  </div>
                  <div className="col-span-3 text-right">
                    <span className="inline-block min-w-14 text-right font-semibold text-gray-800">
                      {it.quantity ?? "-"}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mb-8">
          <p className="text-sm font-semibold mb-1 text-gray-700">Observações</p>
          <div className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 bg-gray-50 min-h-[72px]">
            {observation?.trim() ? observation : "—"}
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={closeModal}
            className="mt-2 w-56 font-semibold transition py-2 px-4 rounded-md bg-locavibe text-white hover:bg-gray-800"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
